/**
 * 템플릿 스타일시트.
 *
 * **고정 캔버스 + 배율.** 모든 치수를 1920×1080 기준 px 로 쓰고, 바깥에서 캔버스를
 * `transform: scale()` 로 줄이고 늘린다(`renderTemplateStage` 가 배율을 적어 준다).
 * 뷰포트에 반응하는 단위(vw)를 쓰면 같은 템플릿이 전체화면 플레이어와 콘솔 미리보기에서
 * **다르게** 보인다 — 미리보기로 확인하고 배포했더니 화면에서 글자가 잘리는 일이 그래서
 * 생긴다. 고정 캔버스는 어디서 보든 같은 그림을 보장한다.
 *
 * **생김새는 차량 화면(CarPlay)을 따른다.** 사이니지는 몇 미터 밖에서 지나가며 읽는다 —
 * 운전 중에 힐끗 보는 화면과 같은 조건이다. 그래서:
 *
 *   - 검은 바탕에 흰 글자, 한 단 밝은 회색 카드(#1c1c1e). 대비가 가장 높고, 밝은 로비에서도
 *     글자가 바탕에 묻히지 않는다.
 *   - 글자는 크고 굵게. 1080 캔버스에서 가장 작은 글자가 26px, 본문 34~44px, 제목 64~112px.
 *     예전 판은 목록·부서명이 15~18px 라 화면 앞에 서야 읽혔다.
 *   - 색은 뜻에만 쓴다 — 아이콘 타일과 판정(증감·마감·긴급). 장식 그라데이션은 한 화면에 하나.
 *   - 긴 글은 잘리기 전에 작아진다(`render.ts` 의 길이 등급 `so-t1`~`so-t4`). 그래도 넘치는
 *     극단의 길이만 말줄임표로 끊는다.
 *
 * **안드로이드 5.1 웹뷰(Chromium 39)에서도 같은 그림이어야 한다** — 납품 기기가 그것이다.
 * 그 엔진은 grid·flex 의 `gap`·CSS 변수·`inset`·접두사 없는 `animation` 을 모르고, 모르는
 * 선언은 조용히 버린다. 예전 판은 grid 로 짜여 그 기기에서 카드가 세로로 쌓여 화면 밖으로
 * 밀렸고 막대 그래프가 사라졌다. 그래서 배치는 flex 와 여백(margin)으로만, 막대는 절대
 * 위치로, 애니메이션은 `-webkit-` 판을 함께 적는다(`styles.test.ts` 가 지킨다).
 *
 * 글꼴은 템플릿마다 고른 것(`fonts.ts`)을 쓰고, 파일은 받는 쪽(플레이어·콘솔)이 번들에 싣는다.
 * 파일을 못 읽어도 시스템 스택으로 떨어져 화면은 나온다. 안드로이드의 한글 기본 글꼴은 맥보다
 * 넓다 — 한 줄 칸은 그것과 나눔스퀘어 네오(가장 넓다)까지 감안해 잡았다.
 */
import { brandFontRule } from './brand.js';
import { DEFAULT_TEMPLATE_FONT, TEMPLATE_FONT_IDS, templateFontStack } from './fonts.js';
/** 디자인 캔버스 크기. 모든 px 값이 이 기준이다. */
export const CANVAS_WIDTH = 1920;
export const CANVAS_HEIGHT = 1080;
/**
 * 세로 화면의 캔버스 — 가로 캔버스를 세운 크기. 세로로 세운 화면에 가로 캔버스를 넣으면
 * 위아래가 크게 빈 채 가운데 작게 나온다(2026-10-05 출하 검사 사전 시험, 화면 회전 90°).
 * 그래서 세로 화면에는 같은 내용을 위아래로 쌓은 캔버스를 쓴다(아래 `.so-tpl-canvas--portrait`).
 */
export const PORTRAIT_CANVAS_WIDTH = 1080;
export const PORTRAIT_CANVAS_HEIGHT = 1920;
/**
 * 반반 화면의 칸 몫(2026-11-03, `share`) — 그림 칸이 40·60·70% 일 때의 칸 크기와, 좁거나 낮아진 글 칸의 글 크기. 50 은 아래의 기본
 * 규칙이다. 칸 크기는 px 로 적는다(옛 웹뷰는 세로 flex 의 % 기준 크기를 0 으로 읽는다). 본문은 가로 캔버스 1840×852, 세로
 * 캔버스 1016×1692 이고 두 칸 사이는 32px 다.
 */
function splitShareCss() {
    const landscape = '.so-tpl-canvas:not(.so-tpl-canvas--portrait)';
    const portrait = '.so-tpl-canvas--portrait';
    const arrangements = [
        { scope: landscape, layouts: ['auto', 'columns'], axis: 'width', total: 1840 - 32 },
        { scope: landscape, layouts: ['rows'], axis: 'height', total: 852 - 32 },
        { scope: portrait, layouts: ['auto', 'rows'], axis: 'height', total: 1692 - 32 },
        { scope: portrait, layouts: ['columns'], axis: 'width', total: 1016 - 32 },
    ];
    const rules = [];
    for (const share of [40, 60, 70]) {
        for (const { scope, layouts, axis, total } of arrangements) {
            const media = Math.round((total * share) / 100);
            for (const [part, size] of [
                ['media', media],
                ['text', total - media],
            ]) {
                const selector = layouts.map((layout) => `${scope} .so-split--${layout}.so-split--s${share} .so-split__${part}`).join(',\n');
                rules.push(`${selector} { -webkit-flex: 0 0 ${size}px; flex: 0 0 ${size}px; ${axis}: ${size}px; }`);
            }
        }
    }
    return rules.join('\n');
}
/**
 * 구역 나누기의 구역 자리(2026-11-03) — 모양마다, 가로·세로 화면마다. 구역 사이는 32px, 첫 구역이 큰 구역이다(차례는 읽는
 * 차례 — 왼쪽 위부터). 절대 위치로 놓는다 — 옛 웹뷰(Chromium 39)는 grid 가 없고 세로 flex 의 % 기준 크기를 0 으로 읽어,
 * 구역이 넷까지 되는 배치를 flex 로 짜면 화면마다 어긋난다.
 */
export const ZONE_BOXES = {
    halves: {
        landscape: [
            { left: 0, top: 0, width: 904, height: 852 },
            { left: 936, top: 0, width: 904, height: 852 },
        ],
        portrait: [
            { left: 0, top: 0, width: 1016, height: 830 },
            { left: 0, top: 862, width: 1016, height: 830 },
        ],
    },
    'main-side': {
        landscape: [
            { left: 0, top: 0, width: 1206, height: 852 },
            { left: 1238, top: 0, width: 602, height: 852 },
        ],
        portrait: [
            { left: 0, top: 0, width: 1016, height: 1106 },
            { left: 0, top: 1138, width: 1016, height: 554 },
        ],
    },
    'main-pair': {
        landscape: [
            { left: 0, top: 0, width: 1206, height: 852 },
            { left: 1238, top: 0, width: 602, height: 410 },
            { left: 1238, top: 442, width: 602, height: 410 },
        ],
        portrait: [
            { left: 0, top: 0, width: 1016, height: 1106 },
            { left: 0, top: 1138, width: 492, height: 554 },
            { left: 524, top: 1138, width: 492, height: 554 },
        ],
    },
    thirds: {
        landscape: [
            { left: 0, top: 0, width: 592, height: 852 },
            { left: 624, top: 0, width: 592, height: 852 },
            { left: 1248, top: 0, width: 592, height: 852 },
        ],
        portrait: [
            { left: 0, top: 0, width: 1016, height: 543 },
            { left: 0, top: 575, width: 1016, height: 542 },
            { left: 0, top: 1149, width: 1016, height: 543 },
        ],
    },
    quad: {
        landscape: [
            { left: 0, top: 0, width: 904, height: 410 },
            { left: 936, top: 0, width: 904, height: 410 },
            { left: 0, top: 442, width: 904, height: 410 },
            { left: 936, top: 442, width: 904, height: 410 },
        ],
        portrait: [
            { left: 0, top: 0, width: 492, height: 830 },
            { left: 524, top: 0, width: 492, height: 830 },
            { left: 0, top: 862, width: 492, height: 830 },
            { left: 524, top: 862, width: 492, height: 830 },
        ],
    },
};
/**
 * 구역의 자리와, 글 구역의 글 크기 — 구역이 작으면 글도 작게(반쪽 칸 904×852 를 1 로 잰 배율). 가장 작은 글자는 26px 를
 * 지킨다. 낮은 구역(560px 밑)은 제목 두 줄·설명 세 줄까지.
 */
function zonesCss() {
    const scopes = {
        landscape: '.so-tpl-canvas:not(.so-tpl-canvas--portrait)',
        portrait: '.so-tpl-canvas--portrait',
    };
    const title = [88, 72, 60, 50];
    const lead = [40, 34, 30, 28];
    const rules = [];
    for (const [layout, boxes] of Object.entries(ZONE_BOXES)) {
        for (const orientation of ['landscape', 'portrait']) {
            boxes[orientation].forEach((box, index) => {
                const zone = `${scopes[orientation]} .so-zones--${layout} .so-zone--${index}`;
                rules.push(`${zone} { left: ${box.left}px; top: ${box.top}px; width: ${box.width}px; height: ${box.height}px; }`);
                const scale = Math.min(1, box.width / 904, box.height / 760);
                const px = (base, floor) => Math.max(floor, Math.round(base * scale));
                rules.push(`${zone}.so-zone--text { padding: ${px(56, 28)}px ${px(64, 32)}px; }`);
                title.forEach((size, tier) => rules.push(`${zone} .so-zone__title.so-t${tier + 1} { font-size: ${px(size, 30)}px; }`));
                lead.forEach((size, tier) => rules.push(`${zone} .so-zone__lead.so-t${tier + 1} { font-size: ${px(size, 26)}px; }`));
                if (scale < 0.8)
                    rules.push(`${zone} .so-badge { padding: 8px 22px; font-size: 26px; }`);
                if (box.height < 560) {
                    rules.push(`${zone} .so-zone__title { -webkit-line-clamp: 2; }`);
                    rules.push(`${zone} .so-zone__lead { -webkit-line-clamp: 3; }`);
                }
            });
        }
    }
    return rules.join('\n');
}
/**
 * 나타날 때의 움직임(2026-11-03) — 템플릿이 화면에 올라올 때 한 번만: 머리글이 내려앉고, 카드가 차례로 떠오르고, 목록·막대·
 * 진행 막대·큰 숫자가 뒤따른다. 1.4초 안에 모두 제자리에 멈춘다 — 이 움직임은 한 번뿐이고, 그 뒤 떠 있는 동안의 은은한
 * 움직임은 `so-live`(2026-11-04, `liveCss`)가 맡는다. 투명도·변형만 움직여 배치는 바뀌지 않는다.
 *
 * 받는 쪽이 무대의 바깥에 `so-enter` 를 달 때만 돈다 — 플레이어는 새로 띄울 때 달고 끝나면 뗀다(같은 콘텐츠를 그대로 둘 때·
 * 시각 때문에 다시 그릴 때는 달지 않는다), 콘솔은 열 때 한 번. 목록 썸네일·[화면 편집] 사본에는 달지 않는다.
 */
export const TEMPLATE_ENTER_CLASS = 'so-enter';
function enterCss() {
    const scope = `.${TEMPLATE_ENTER_CLASS}`;
    const ease = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
    const both = (name, ms, delayMs) => `-webkit-animation: ${name} ${ms}ms ${ease} ${delayMs}ms both; animation: ${name} ${ms}ms ${ease} ${delayMs}ms both;`;
    const rules = [];
    const keyframes = [
        ['so-in-up', 'opacity: 0; -webkit-transform: translate3d(0, 28px, 0); transform: translate3d(0, 28px, 0);', 'opacity: 1; -webkit-transform: none; transform: none;'],
        ['so-in-down', 'opacity: 0; -webkit-transform: translate3d(0, -20px, 0); transform: translate3d(0, -20px, 0);', 'opacity: 1; -webkit-transform: none; transform: none;'],
        ['so-in-fade', 'opacity: 0;', 'opacity: 1;'],
        ['so-in-pop', 'opacity: 0; -webkit-transform: scale(0.9); transform: scale(0.9);', 'opacity: 1; -webkit-transform: none; transform: none;'],
        ['so-grow-x', '-webkit-transform: scaleX(0); transform: scaleX(0);', '-webkit-transform: scaleX(1); transform: scaleX(1);'],
        ['so-grow-y', '-webkit-transform: scaleY(0); transform: scaleY(0);', '-webkit-transform: scaleY(1); transform: scaleY(1);'],
    ];
    for (const [name, from, to] of keyframes) {
        rules.push(`@-webkit-keyframes ${name} { from { ${from} } to { ${to} } }`);
        rules.push(`@keyframes ${name} { from { ${from} } to { ${to} } }`);
    }
    rules.push(`${scope} .so-tpl__header { ${both('so-in-down', 500, 0)} }`);
    rules.push(`${scope} .so-tpl__footer { ${both('so-in-fade', 500, 300)} }`);
    // 본문의 큰 덩어리(카드·칸·구역) — 차례로 떠오른다.
    for (let n = 1; n <= 6; n++)
        rules.push(`${scope} .so-body > :nth-child(${n}) { ${both('so-in-up', 700, 120 + (n - 1) * 100)} }`);
    // 덩어리 안의 줄·칸 — 덩어리 뒤를 잇는다.
    const items = [
        '.so-ann__notice',
        '.so-promo__benefit',
        '.so-alert__stat',
        '.so-alert__action',
        '.so-sched__event',
        '.so-sched__upcoming-item',
        '.so-sched__stat',
        '.so-dash__kpi',
        '.so-dash__row',
        '.so-split__point',
        '.so-zero__kpi',
    ];
    for (let n = 1; n <= 8; n++) {
        rules.push(`${items.map((item) => `${scope} ${item}:nth-child(${n})`).join(',\n')} { ${both('so-in-up', 500, 350 + (n - 1) * 60)} }`);
    }
    // 막대는 바닥에서 자라고, 진행 막대는 왼쪽에서 채워진다 — 값이 눈에 들어온다.
    rules.push(`${scope} .so-dash__bar { -webkit-transform-origin: 50% 100%; transform-origin: 50% 100%; ${both('so-grow-y', 800, 450)} }`);
    rules.push(`${scope} .so-dash__progress-fill,\n${scope} .so-zero__fill { -webkit-transform-origin: 0 50%; transform-origin: 0 50%; ${both('so-grow-x', 900, 500)} }`);
    // 큰 숫자·배지 — 살짝 커지며 자리 잡는다.
    rules.push(`${scope} .so-zero__dday-value,\n${scope} .so-promo__amount-value,\n${scope} .so-alert__icon,\n${scope} .so-badge { ${both('so-in-pop', 600, 400)} }`);
    return rules.join('\n');
}
/**
 * 떠 있는 동안의 움직임(2026-11-04 사용자 "화면이 떠 있는 동안 눈에 띄도록") — 나타나는 움직임(`so-enter`)이 끝난 뒤 붙는
 * `so-live` 아래에서 요소마다 성격에 맞게 계속 움직인다.
 *
 *   - 진행 막대: 채운 부분 위로 빛줄기가 흐른다 · 막대 그래프: 바닥에서 위로 빛이 차오르고, 막대마다 조금씩 늦어 물결이 된다
 *   - 카드·칸: 빛이 비스듬히 한 번 스치고 쉰다(카드마다 차례로) · 목록 줄: 한 줄씩 차례로 밝아지며 왼쪽에 띠가 선다
 *   - 아이콘 타일: 둥실 떠오른다 · 늘었다·줄었다 화살표: 그 방향으로 살짝 · 배지: 테두리가 퍼져 나간다 · 경보 아이콘: 물결
 *   - 큰 숫자(D-day·지원금·오늘 날짜): 가끔 한 번 살짝 커진다 · 꽉 채운 사진: 아주 천천히 다가간다
 *
 * **읽는 데 방해하지 않는다.** 글은 흔들지 않는다(큰 숫자만 8% 안쪽으로 한 번씩). 빛은 글 뒤로 지나간다 — 카드·줄을 쌓임
 * 맥락으로 만들고 빛을 그 안의 맨 뒤(`z-index: -1`)에 둔다. 투명도·변형만 움직여 배치는 바뀌지 않고 합성 단계에서 돈다.
 * 정비 화면처럼 손으로 누르는 화면은 아니라 끝없는 움직임을 쓴다(티커와 같다). 동작 줄이기를 켠 화면은 멈춘다.
 *
 * 플레이어는 나타나는 움직임이 끝나면 `so-live` 를 달고(시각 때문에 다시 그릴 때는 곧바로), 콘솔의 살아 있는 미리보기도
 * 같다. 목록 썸네일·[화면 편집] 사본에는 달지 않는다.
 */
export const TEMPLATE_LIVE_CLASS = 'so-live';
/**
 * 움직임을 그 자리에 멈춘다(2026-11-04) — 플레이어가 화면을 바꾸는 동안 나가는 템플릿에 단다. 사라질 화면이 계속 그려 달라고
 * 하면 들어오는 화면의 전환·나타나는 움직임과 그리기를 다툰다(그리기가 느린 기기에서 전환이 끊겼다). 멈춘 모습 그대로라 튀지 않는다.
 */
export const TEMPLATE_PAUSE_CLASS = 'so-pause';
function liveCss() {
    const scope = `.${TEMPLATE_LIVE_CLASS}`;
    const loop = (name, ms, timing, delayMs = 0, extra = '') => {
        const value = `${name} ${ms}ms ${timing} ${delayMs}ms infinite${extra ? ` ${extra}` : ''} both`;
        return `-webkit-animation: ${value}; animation: ${value};`;
    };
    const delay = (ms) => `-webkit-animation-delay: ${ms}ms; animation-delay: ${ms}ms;`;
    const t = (value) => `-webkit-transform: ${value}; transform: ${value};`;
    const rules = [];
    const keyframes = [
        // 진행 막대의 빛줄기 — 앞 40% 에 지나가고 쉰다.
        ['so-live-sheen', `0% { ${t('translate3d(-100%, 0, 0)')} } 40%, 100% { ${t('translate3d(250%, 0, 0)')} }`],
        // 막대 그래프 — 바닥 아래에서 꼭대기 위로.
        ['so-live-rise', `0% { ${t('translate3d(0, 100%, 0)')} } 45%, 100% { ${t('translate3d(0, -240%, 0)')} }`],
        // 카드를 비스듬히 스치는 빛 — 앞 14% 에 지나가고 오래 쉰다.
        ['so-live-glint', `0% { ${t('translate3d(-160%, 0, 0) skewX(-20deg)')} } 14%, 100% { ${t('translate3d(420%, 0, 0) skewX(-20deg)')} }`],
        // 목록 줄의 차례 — 제 차례에 밝아졌다가 사그라든다.
        ['so-live-spot', '0% { opacity: 0; } 5%, 13% { opacity: 1; } 20%, 100% { opacity: 0; }'],
        ['so-live-float', `0%, 100% { ${t('translate3d(0, 0, 0)')} } 50% { ${t('translate3d(0, -14px, 0)')} }`],
        ['so-live-nudge-up', `0%, 60%, 100% { ${t('translate3d(0, 0, 0)')} } 75% { ${t('translate3d(0, -10px, 0)')} }`],
        ['so-live-nudge-down', `0%, 60%, 100% { ${t('translate3d(0, 0, 0)')} } 75% { ${t('translate3d(0, 10px, 0)')} }`],
        ['so-live-ping', `0% { opacity: 0.9; ${t('scale(1, 1)')} } 60%, 100% { opacity: 0; ${t('scale(1.2, 1.7)')} }`],
        ['so-live-ripple', `0% { opacity: 0.9; ${t('scale(1)')} } 70%, 100% { opacity: 0; ${t('scale(1.8)')} }`],
        ['so-live-beat', `0%, 30%, 100% { ${t('scale(1)')} } 12% { ${t('scale(1.08)')} }`],
        ['so-live-zoom', `from { ${t('scale(1)')} } to { ${t('scale(1.1)')} }`],
    ];
    for (const [name, body] of keyframes) {
        rules.push(`@-webkit-keyframes ${name} { ${body} }`);
        rules.push(`@keyframes ${name} { ${body} }`);
    }
    const layer = 'content: \'\'; position: absolute; pointer-events: none;';
    // 진행 막대 — 채운 부분 안을 흐르는 빛. 줄마다 조금씩 늦게.
    rules.push(`${scope} .so-dash__progress-fill,\n${scope} .so-zero__fill { position: relative; overflow: hidden; }`);
    rules.push(`${scope} .so-dash__progress-fill:after,\n${scope} .so-zero__fill:after { ${layer} top: 0; bottom: 0; left: 0; width: 45%; ` +
        `background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.75), rgba(255,255,255,0)); ${t('translate3d(-100%, 0, 0)')} ` +
        `${loop('so-live-sheen', 2600, 'ease-in-out')} }`);
    for (let n = 2; n <= 6; n++)
        rules.push(`${scope} .so-dash__progress-item:nth-child(${n}) .so-dash__progress-fill:after { ${delay((n - 1) * 300)} }`);
    // 막대 그래프 — 바닥에서 차오르는 빛, 막대 묶음마다 늦어 물결이 된다.
    rules.push(`${scope} .so-dash__bar { overflow: hidden; }`);
    rules.push(`${scope} .so-dash__bar:after { ${layer} left: 0; right: 0; bottom: 0; height: 45%; ` +
        `background: linear-gradient(0deg, rgba(255,255,255,0), rgba(255,255,255,0.6), rgba(255,255,255,0)); ${t('translate3d(0, 100%, 0)')} ` +
        `${loop('so-live-rise', 3000, 'ease-in-out')} }`);
    for (let n = 2; n <= 12; n++)
        rules.push(`${scope} .so-dash__bar-group:nth-child(${n}) .so-dash__bar:after { ${delay((n - 1) * 140)} }`);
    /*
     * 카드·칸 — 빛이 비스듬히 스친다. 상자를 쌓임 맥락으로 만들고 빛을 그 안의 맨 뒤에 둬 글 뒤로 지나간다. 구역(`so-zone`)은
     * 이미 제자리에 박혀(absolute) 있어 자리 방식을 건드리지 않는다.
     */
    const glow = ['.so-card', '.so-promo__visual', '.so-sched__today'];
    rules.push(`${glow.map((item) => `${scope} ${item}`).join(',\n')} { z-index: 0; overflow: hidden; }`);
    rules.push(`${glow.map((item) => `${scope} ${item}:not(.so-zone)`).join(',\n')} { position: relative; }`);
    rules.push(`${glow.map((item) => `${scope} ${item}:after`).join(',\n')} { ${layer} z-index: -1; top: -10%; bottom: -10%; left: 0; width: 36%; ` +
        `background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.2), rgba(255,255,255,0)); ${t('translate3d(-160%, 0, 0) skewX(-20deg)')} ` +
        `${loop('so-live-glint', 6000, 'ease-in-out', 600)} }`);
    for (let n = 2; n <= 6; n++) {
        rules.push(`${glow.map((item) => `${scope} ${item}:nth-child(${n}):after`).join(',\n')} { ${delay(600 + (n - 1) * 900)} }`);
    }
    // 목록 줄 — 한 줄씩 차례로 밝아지며 왼쪽에 흰 띠가 선다(8줄 한 바퀴 9.6초). 빛은 줄의 글 뒤에.
    const rows = [
        '.so-ann__notice',
        '.so-promo__benefit',
        '.so-alert__action',
        '.so-sched__event',
        '.so-sched__upcoming-item',
        '.so-dash__row',
        '.so-split__point',
    ];
    rules.push(`${rows.map((item) => `${scope} ${item}`).join(',\n')} { position: relative; z-index: 0; }`);
    rules.push(`${rows.map((item) => `${scope} ${item}:after`).join(',\n')} { ${layer} z-index: -1; top: 0; right: 0; bottom: 0; left: 0; ` +
        `border-radius: inherit; background: rgba(255,255,255,0.16); box-shadow: inset 8px 0 0 rgba(255,255,255,0.7); opacity: 0; ${loop('so-live-spot', 9600, 'ease-in-out')} }`);
    for (let n = 2; n <= 8; n++) {
        rules.push(`${rows.map((item) => `${scope} ${item}:nth-child(${n}):after`).join(',\n')} { ${delay((n - 1) * 1200)} }`);
    }
    // 아이콘 타일 — 둥실. 줄마다 엇갈리게.
    rules.push(`${scope} .so-tile-icon { ${loop('so-live-float', 3200, 'ease-in-out')} }`);
    for (let n = 2; n <= 8; n++) {
        const parents = ['.so-promo__benefit', '.so-alert__action', '.so-split__point', '.so-dash__kpi'];
        rules.push(`${parents.map((item) => `${scope} ${item}:nth-child(${n}) .so-tile-icon`).join(',\n')} { ${delay((n - 1) * 450)} }`);
    }
    // 늘었다·줄었다 — 화살표가 그 방향으로 살짝.
    rules.push(`${scope} .so-dash__kpi-change--up .so-i { ${loop('so-live-nudge-up', 2400, 'ease-in-out')} }`);
    rules.push(`${scope} .so-dash__kpi-change--down .so-i { ${loop('so-live-nudge-down', 2400, 'ease-in-out')} }`);
    // 배지 — 테두리가 퍼져 나간다(배지 뒤에서).
    rules.push(`${scope} .so-badge { position: relative; z-index: 0; }`);
    rules.push(`${scope} .so-badge:after { ${layer} z-index: -1; top: 0; right: 0; bottom: 0; left: 0; border-radius: inherit; ` +
        `border: 4px solid rgba(255,255,255,0.85); opacity: 0; ${loop('so-live-ping', 2200, 'ease-out', 400)} }`);
    // 경보 아이콘 — 원 바깥으로 물결.
    rules.push(`${scope} .so-alert__icon-core { position: relative; z-index: 0; }`);
    rules.push(`${scope} .so-alert__icon-core:after { ${layer} z-index: -1; top: 0; right: 0; bottom: 0; left: 0; border-radius: 50%; ` +
        `border: 8px solid #ff9f0a; opacity: 0; ${loop('so-live-ripple', 1800, 'ease-out')} }`);
    // 큰 숫자 — 가끔 한 번 살짝 커진다. 붙은 쪽(오른쪽·왼쪽·가운데)을 축으로.
    rules.push(`${scope} .so-zero__dday-value { -webkit-transform-origin: 100% 50%; transform-origin: 100% 50%; ${loop('so-live-beat', 3000, 'ease-in-out', 800)} }`);
    rules.push(`${scope} .so-promo__amount-value { -webkit-transform-origin: 0 50%; transform-origin: 0 50%; ${loop('so-live-beat', 3000, 'ease-in-out', 800)} }`);
    rules.push(`${scope} .so-sched__date-day { ${loop('so-live-beat', 3000, 'ease-in-out', 800)} }`);
    // 꽉 채운 사진 — 아주 천천히 다가갔다 물러난다. 다 보이게 맞춘 사진은 가장자리가 움직여 보여 두지 않는다.
    rules.push(`${scope} .so-split__image.so-fit-cover { ${loop('so-live-zoom', 12000, 'ease-in-out', 0, 'alternate')} }`);
    // 멈춤 — 나가는 화면(`TEMPLATE_PAUSE_CLASS`). 티커·깜박이는 점까지 그 자리에.
    const pause = `.${TEMPLATE_PAUSE_CLASS}`;
    rules.push(`${pause} *,\n${pause} *:after { -webkit-animation-play-state: paused; animation-play-state: paused; }`);
    return rules.join('\n');
}
export const TEMPLATE_CSS = `
.so-tpl-stage {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: center;
  justify-content: center;
  overflow: hidden;
  background: #000;
}

/* 캔버스. 배율은 렌더러가 style 로 적는다 — transform 은 배치를 다시 하지 않아 큰 화면에서도 싸다. */
.so-tpl-canvas {
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
  -webkit-flex: none;
  flex: none;
  -webkit-transform-origin: center center;
  transform-origin: center center;
  font-family: ${templateFontStack(DEFAULT_TEMPLATE_FONT)};
  -webkit-font-smoothing: antialiased;
  color: #fff;
  line-height: 1.25;
  word-break: keep-all;
}

.so-tpl-canvas--portrait {
  width: ${PORTRAIT_CANVAS_WIDTH}px;
  height: ${PORTRAIT_CANVAS_HEIGHT}px;
}

/* 고른 글꼴 — 무대에 붙은 이름(\`so-font-*\`)으로. 파일은 받는 쪽이 \`templateFontFaceCss\` 로 싣는다. */
${TEMPLATE_FONT_IDS.map((id) => `.so-font-${id} .so-tpl-canvas { font-family: ${templateFontStack(id)}; }`).join('\n')}
${brandFontRule()}

.so-tpl-canvas *,
.so-tpl-canvas *:before,
.so-tpl-canvas *:after { box-sizing: border-box; }

.so-tpl-canvas h1,
.so-tpl-canvas h2,
.so-tpl-canvas h3,
.so-tpl-canvas h4,
.so-tpl-canvas p { margin: 0; }

.so-tpl {
  width: 100%;
  height: 100%;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
  background: #000;
  color: #fff;
}

.so-i { width: 1em; height: 1em; -webkit-flex: none; flex: none; }

/* 한 줄로 끊는 칸 — 그래도 넘치는 극단의 길이만 말줄임표. */
.so-one { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 두세 줄까지 — 넘치면 끝에 말줄임표. Chromium 39 도 -webkit-box 로 안다. */
.so-clamp2,
.so-clamp3,
.so-clamp4 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.so-clamp2 { -webkit-line-clamp: 2; }
.so-clamp3 { -webkit-line-clamp: 3; }
.so-clamp4 { -webkit-line-clamp: 4; }

/* ── 머리 ─────────────────────────────────────────────────────────── */
.so-tpl__header {
  -webkit-flex: none;
  flex: none;
  height: 104px;
  padding: 0 56px;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: space-between;
  justify-content: space-between;
  color: #fff;
}

.so-tpl__header--alert { border-bottom: 3px solid #ff9f0a; }

.so-tpl__brand {
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: baseline;
  align-items: baseline;
  min-width: 0;
  -webkit-flex: 1;
  flex: 1;
  overflow: hidden;
}

/* 로고는 글자 높이에 맞춘다 — 가로로 긴 로고가 장소 이름을 밀어내지 않게 폭도 막는다. */
.so-tpl__brand-logo {
  -webkit-flex: none;
  flex: none;
  -webkit-align-self: center;
  align-self: center;
  height: 56px;
  max-width: 320px;
  margin-right: 24px;
  object-fit: contain;
}

.so-tpl__brand-name { -webkit-flex: none; flex: none; font-size: 30px; font-weight: 600; color: rgba(235,235,245,0.6); white-space: nowrap; }
.so-tpl__brand-sep { -webkit-flex: none; flex: none; margin: 0 20px; font-size: 30px; color: rgba(235,235,245,0.3); }
.so-tpl__brand-place { min-width: 0; font-size: 42px; font-weight: 800; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.so-tpl__clock {
  -webkit-flex: none;
  flex: none;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  margin-left: 32px;
}

.so-tpl__clock > * + * { margin-left: 28px; }
.so-tpl__date { font-size: 30px; font-weight: 500; color: rgba(235,235,245,0.6); white-space: nowrap; }
.so-tpl__time { font-size: 60px; font-weight: 800; letter-spacing: -0.01em; white-space: nowrap; }

.so-tpl__live {
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  padding: 6px 18px;
  border-radius: 999px;
  background: rgba(48,209,88,0.18);
  color: #30d158;
  font-size: 26px;
  font-weight: 800;
}

.so-tpl__live-dot {
  width: 14px;
  height: 14px;
  margin-right: 10px;
  border-radius: 50%;
  background: #30d158;
  -webkit-animation: so-blink 1.2s ease-in-out infinite;
  animation: so-blink 1.2s ease-in-out infinite;
}

/* ── 본문 ─────────────────────────────────────────────────────────── */
.so-tpl__main {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  padding: 4px 40px 28px;
  display: -webkit-flex;
  display: flex;
  overflow: hidden;
}

/* 템플릿마다의 뿌리 — 본문을 가득 채운다. */
.so-body {
  -webkit-flex: 1;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
}

.so-col {
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* ── 아래 띠(티커) ────────────────────────────────────────────────── */
.so-tpl__footer {
  -webkit-flex: none;
  flex: none;
  height: 92px;
  padding: 0 40px;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  background: #1c1c1e;
  color: #fff;
}

.so-tpl__footer--alert { background: #2b1d05; }

.so-tpl__ticker {
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-flex: 1;
  flex: 1;
  min-width: 0;
}

.so-tpl__ticker-label {
  -webkit-flex: none;
  flex: none;
  margin-right: 28px;
  padding: 8px 22px;
  border-radius: 14px;
  background: #0a84ff;
  font-size: 28px;
  font-weight: 800;
  white-space: nowrap;
}

.so-tpl__footer--alert .so-tpl__ticker-label { background: #ff9f0a; color: #000; }

.so-tpl__ticker-track { -webkit-flex: 1; flex: 1; min-width: 0; overflow: hidden; font-size: 34px; font-weight: 600; }

/*
 * 흐르는 글. 내용을 두 벌 쓰고(렌더러) 자기 폭의 절반만큼 움직여 이음매가 없다 — 그래서
 * 블록이 아니라 inline-block 이다(블록이면 -50% 가 칸 폭의 절반이 되어 툭 끊긴다).
 * 속도는 글 길이에 맞춰 렌더러가 적는다.
 */
.so-tpl__ticker-move {
  display: inline-block;
  white-space: nowrap;
  -webkit-animation: so-ticker 30s linear infinite;
  animation: so-ticker 30s linear infinite;
}

.so-tpl__temp { -webkit-flex: none; flex: none; margin-left: 28px; font-size: 36px; font-weight: 800; }

@-webkit-keyframes so-ticker {
  from { -webkit-transform: translateX(0); transform: translateX(0); }
  to { -webkit-transform: translateX(-50%); transform: translateX(-50%); }
}

@keyframes so-ticker {
  from { -webkit-transform: translateX(0); transform: translateX(0); }
  to { -webkit-transform: translateX(-50%); transform: translateX(-50%); }
}

@-webkit-keyframes so-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
@keyframes so-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

@media (prefers-reduced-motion: reduce) {
  .so-enter *,
  .so-live *,
  .so-live *:after,
  .so-tpl__ticker-move,
  .so-tpl__live-dot,
  .so-alert__eyebrow-dot,
  .so-dash__live-dot { -webkit-animation: none; animation: none; }
}

/* ── 공용 조각 ────────────────────────────────────────────────────── */
.so-card { background: #1c1c1e; border-radius: 36px; }
.so-card--glass { background: #1c1c1e; border-radius: 32px; }

.so-badge {
  display: -webkit-inline-flex;
  display: inline-flex;
  -webkit-align-items: center;
  align-items: center;
  padding: 10px 28px;
  border-radius: 999px;
  background: #0a84ff;
  color: #fff;
  font-size: 32px;
  font-weight: 800;
  white-space: nowrap;
  max-width: 100%;
}

.so-badge .so-i { margin-right: 12px; }
.so-badge .so-one { min-width: 0; }
.so-badge--glass { background: rgba(255,255,255,0.22); }

/* 아이콘 타일 — 둥근 네모 안에 흰 그림(차량 화면의 앱 아이콘). */
.so-tile-icon {
  -webkit-flex: none;
  flex: none;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: center;
  justify-content: center;
  color: #fff;
}

/* ── 공지사항 ─────────────────────────────────────────────────────── */
.so-ann__main {
  -webkit-flex: 1;
  flex: 1;
  min-width: 0;
  padding: 56px 64px 48px;
}

.so-ann__body {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-justify-content: center;
  justify-content: center;
  padding: 24px 0;
  overflow: hidden;
}

.so-ann__title { font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; }
.so-ann__title.so-t1 { font-size: 112px; }
.so-ann__title.so-t2 { font-size: 96px; }
.so-ann__title.so-t3 { font-size: 80px; }
.so-ann__title.so-t4 { font-size: 64px; }

.so-ann__lead { margin-top: 28px; color: rgba(235,235,245,0.7); line-height: 1.45; font-weight: 500; }
.so-ann__lead.so-t1 { font-size: 44px; }
.so-ann__lead.so-t2 { font-size: 38px; }
.so-ann__lead.so-t3 { font-size: 32px; }
.so-ann__lead.so-t4 { font-size: 28px; }

.so-ann__meta {
  -webkit-flex: none;
  flex: none;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  padding-top: 28px;
  border-top: 2px solid #38383a;
}

.so-ann__meta-item {
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  min-width: 0;
  font-size: 34px;
  font-weight: 600;
  color: rgba(235,235,245,0.8);
}

.so-ann__meta-item:first-child { -webkit-flex: none; flex: none; }
.so-ann__meta-item + .so-ann__meta-item { margin-left: 56px; }
.so-ann__meta-item .so-i { margin-right: 14px; color: #0a84ff; }

.so-ann__side { -webkit-flex: 0 0 600px; flex: 0 0 600px; width: 600px; margin-left: 32px; }

.so-ann__notices {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  padding: 36px 36px 28px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-ann__notices h3 { -webkit-flex: none; flex: none; margin-bottom: 20px; font-size: 34px; font-weight: 800; }

/* 공지는 남은 높이를 나눠 갖는다 — 여섯 개여도 마지막 줄이 반쯤 잘려 남지 않게. */
.so-ann__notice-list {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-ann__notice {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  max-height: 124px;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-justify-content: center;
  justify-content: center;
  border-top: 2px solid #38383a;
  overflow: hidden;
}
.so-ann__notice-title { display: block; font-size: 34px; font-weight: 700; }
.so-ann__notice-dept { display: block; margin-top: 4px; font-size: 26px; font-weight: 500; color: rgba(235,235,245,0.6); }

.so-ann__weather {
  -webkit-flex: none;
  flex: none;
  margin-top: 24px;
  padding: 28px 36px;
  border-radius: 36px;
  background: #0a84ff;
  background: linear-gradient(135deg, #0a84ff, #0060df);
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: space-between;
  justify-content: space-between;
}

.so-ann__weather-main { -webkit-flex: 1; flex: 1; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; min-width: 0; }
.so-ann__weather-text { min-width: 0; }
.so-ann__weather-main .so-i { margin-right: 20px; font-size: 76px; color: #ffd60a; }
.so-ann__weather-temp { font-size: 64px; font-weight: 800; line-height: 1; }
.so-ann__weather-cond { margin-top: 6px; font-size: 28px; font-weight: 600; opacity: 0.9; }
.so-ann__weather-place { -webkit-flex: 0 1 auto; flex: 0 1 auto; max-width: 40%; min-width: 0; margin-left: 20px; font-size: 30px; font-weight: 700; text-align: right; }

/* ── 프로모션 ─────────────────────────────────────────────────────── */
.so-promo__visual {
  position: relative;
  -webkit-flex: 0 0 54%;
  flex: 0 0 54%;
  width: 54%;
  min-width: 0;
  border-radius: 36px;
  overflow: hidden;
  background: #5e5ce6;
  background: linear-gradient(135deg, #0a84ff, #5e5ce6 45%, #ff375f);
  padding: 52px 56px;
}

.so-promo__headline {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-justify-content: center;
  justify-content: center;
  padding: 24px 0;
  overflow: hidden;
}

.so-promo__title { font-weight: 800; letter-spacing: -0.02em; line-height: 1.12; }
.so-promo__title.so-t1 { font-size: 104px; }
.so-promo__title.so-t2 { font-size: 88px; }
.so-promo__title.so-t3 { font-size: 72px; }
.so-promo__title.so-t4 { font-size: 56px; }
.so-promo__title em { display: block; font-style: normal; color: #ffd60a; }

.so-promo__lead { margin-top: 24px; font-weight: 600; line-height: 1.4; opacity: 0.92; }
.so-promo__lead.so-t1 { font-size: 40px; }
.so-promo__lead.so-t2 { font-size: 34px; }
.so-promo__lead.so-t3 { font-size: 30px; }
.so-promo__lead.so-t4 { font-size: 28px; }

.so-promo__qr { -webkit-flex: none; flex: none; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; }
.so-promo__qr-box { -webkit-flex: none; flex: none; margin-right: 28px; padding: 12px; border-radius: 22px; background: #fff; line-height: 0; }
.so-promo__qr-box svg { width: 156px; height: 156px; color: #000; }
.so-promo__qr-text { -webkit-flex: 1; flex: 1; min-width: 0; }
.so-promo__qr-caption { font-size: 38px; font-weight: 800; }
.so-promo__qr-hint { margin-top: 6px; font-size: 28px; font-weight: 500; opacity: 0.85; }

.so-promo__side { -webkit-flex: 1; flex: 1; margin-left: 32px; }

.so-promo__amount { -webkit-flex: none; flex: none; padding: 32px 44px; }
.so-promo__amount-label { font-size: 30px; font-weight: 600; color: rgba(235,235,245,0.6); }

.so-promo__amount-value {
  margin-top: 4px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: #64d2ff;
  background: linear-gradient(90deg, #64d2ff, #bf5af2);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.so-promo__amount-value.so-t1 { font-size: 96px; }
.so-promo__amount-value.so-t2 { font-size: 80px; }
.so-promo__amount-value.so-t3 { font-size: 64px; }
.so-promo__amount-value.so-t4 { font-size: 52px; }

.so-promo__benefits {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  margin-top: 20px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-promo__benefit {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  max-height: 156px;
  min-height: 0;
  padding: 0 36px;
  border-radius: 28px;
  background: #1c1c1e;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  overflow: hidden;
}

.so-promo__benefit + .so-promo__benefit { margin-top: 16px; }

.so-promo__benefit-icon {
  width: 84px;
  height: 84px;
  margin-right: 28px;
  border-radius: 22px;
  background: #5e5ce6;
  background: linear-gradient(135deg, #0a84ff, #bf5af2);
  font-size: 44px;
}

.so-promo__benefit-text { min-width: 0; -webkit-flex: 1; flex: 1; }
.so-promo__benefit-title { font-size: 40px; font-weight: 800; }
.so-promo__benefit-desc { margin-top: 4px; font-size: 30px; font-weight: 500; color: rgba(235,235,245,0.6); }

.so-promo__countdown { -webkit-flex: none; flex: none; margin-top: 20px; padding: 24px 32px 20px; border-radius: 32px; background: #2c0f0d; }
.so-promo__countdown-label { font-size: 30px; font-weight: 800; color: #ff453a; text-align: center; }
.so-promo__countdown-row { margin-top: 6px; display: -webkit-flex; display: flex; -webkit-justify-content: center; justify-content: center; }
.so-promo__countdown-row > div { width: 140px; text-align: center; }
.so-promo__countdown-value { font-size: 76px; font-weight: 800; line-height: 1.05; }
.so-promo__countdown-unit { font-size: 26px; font-weight: 600; color: rgba(235,235,245,0.6); }

/* ── 긴급알림 ─────────────────────────────────────────────────────── */
.so-tpl--alert { background: #000; }

.so-alert__head { -webkit-flex: none; flex: none; padding-top: 24px; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; }

.so-alert__icon {
  -webkit-flex: none;
  flex: none;
  width: 240px;
  margin-right: 44px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-align-items: center;
  align-items: center;
}

.so-alert__icon-core {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: #ff9f0a;
  background: linear-gradient(135deg, #ffd60a, #ff9f0a);
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: center;
  justify-content: center;
  font-size: 92px;
  color: #000;
  box-shadow: 0 0 0 14px rgba(255,159,10,0.18);
}

.so-alert__icon-label { width: 100%; margin-top: 30px; font-size: 32px; font-weight: 800; color: #ff9f0a; text-align: center; }

.so-alert__heading { -webkit-flex: 1; flex: 1; min-width: 0; }
.so-alert__eyebrow { display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; margin-bottom: 12px; }

.so-alert__eyebrow-dot {
  width: 22px;
  height: 22px;
  margin-right: 16px;
  border-radius: 50%;
  background: #ff453a;
  -webkit-animation: so-blink 1s ease-in-out infinite;
  animation: so-blink 1s ease-in-out infinite;
}

.so-alert__eyebrow-text { font-size: 40px; font-weight: 800; color: #ff9f0a; }

.so-alert__title { font-weight: 800; letter-spacing: -0.02em; line-height: 1.12; }
.so-alert__title.so-t1 { font-size: 104px; }
.so-alert__title.so-t2 { font-size: 88px; }
.so-alert__title.so-t3 { font-size: 72px; }
.so-alert__title.so-t4 { font-size: 60px; }

.so-alert__stats { -webkit-flex: none; flex: none; margin-top: 28px; display: -webkit-flex; display: flex; }

.so-alert__stat {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  min-width: 0;
  padding: 24px 40px;
  border-radius: 32px;
  background: #2b1d05;
}

.so-alert__stat + .so-alert__stat { margin-left: 24px; }
.so-alert__stat-label { font-size: 30px; font-weight: 700; color: #ff9f0a; }
.so-alert__stat-value { margin-top: 4px; font-weight: 800; line-height: 1.1; letter-spacing: -0.01em; }
.so-alert__stat-value.so-t1 { font-size: 84px; }
.so-alert__stat-value.so-t2 { font-size: 68px; }
.so-alert__stat-value.so-t3 { font-size: 56px; }
.so-alert__stat-value.so-t4 { font-size: 46px; }

.so-alert__actions {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  margin-top: 24px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-alert__action {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  max-height: 150px;
  min-height: 0;
  padding: 0 40px;
  border-radius: 28px;
  background: #1c1c1e;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  overflow: hidden;
}

.so-alert__action + .so-alert__action { margin-top: 16px; }

.so-alert__action-num {
  width: 76px;
  height: 76px;
  margin-right: 32px;
  border-radius: 22px;
  background: #ff9f0a;
  font-size: 44px;
  font-weight: 800;
  color: #000;
}

.so-alert__action-text { -webkit-flex: 1; flex: 1; min-width: 0; font-weight: 700; line-height: 1.2; }
.so-alert__action-text.so-t1 { font-size: 46px; }
.so-alert__action-text.so-t2 { font-size: 40px; }
.so-alert__action-text.so-t3 { font-size: 34px; }
.so-alert__action-text.so-t4 { font-size: 30px; }

/* ── 일정 ─────────────────────────────────────────────────────────── */
/* 세 단 모두 위아래 끝이 같은 카드다 — 사이 여백은 32px 하나로. */
.so-sched__today {
  -webkit-flex: 0 0 380px;
  flex: 0 0 380px;
  width: 380px;
  border-radius: 36px;
  overflow: hidden;
  background: #0a84ff;
  background: linear-gradient(160deg, #0a84ff, #0050c8);
}

.so-sched__date {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-justify-content: center;
  justify-content: center;
  text-align: center;
}

.so-sched__date-month { font-size: 44px; font-weight: 700; color: rgba(255,255,255,0.8); }
.so-sched__date-day { margin: 4px 0 8px; font-size: 220px; font-weight: 800; line-height: 1; letter-spacing: -0.04em; }
.so-sched__date-weekday { font-size: 52px; font-weight: 800; }

/* D-데이는 오늘 카드 아래 띠 — 날짜와 한 덩어리로 읽힌다. 글자만 노랑으로 눈을 끈다. */
.so-sched__countdown { -webkit-flex: none; flex: none; padding: 28px 36px 32px; background: rgba(0,0,0,0.24); }
.so-sched__countdown-label { font-size: 56px; font-weight: 800; line-height: 1.05; color: #ffd60a; }
.so-sched__countdown-title { margin-top: 6px; font-size: 34px; font-weight: 700; }

.so-sched__list { -webkit-flex: 1; flex: 1; margin-left: 32px; padding: 32px 44px 12px; }

.so-sched__list-head {
  -webkit-flex: none;
  flex: none;
  padding-bottom: 20px;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: baseline;
  align-items: baseline;
}

.so-sched__heading { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-width: 0; font-size: 48px; font-weight: 800; letter-spacing: -0.01em; }
.so-sched__count { -webkit-flex: none; flex: none; margin-left: 20px; font-size: 30px; font-weight: 700; color: rgba(235,235,245,0.6); }

/* 일정 줄은 카드 높이를 나눠 갖는다 — 적어도 아래가 비지 않고, 많아도 넘치지 않는다. */
.so-sched__events {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-sched__event {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  min-height: 0;
  border-top: 2px solid #38383a;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  overflow: hidden;
}

/* 시각은 자릿수 폭을 고정한다(tnum) — 09:00 과 10:30 의 끝이 한 줄에 선다. */
.so-sched__event-time {
  -webkit-flex: none;
  flex: none;
  width: 200px;
  font-size: 60px;
  font-weight: 800;
  color: #64d2ff;
  letter-spacing: -0.02em;
  -webkit-font-feature-settings: 'tnum';
  font-feature-settings: 'tnum';
}

.so-sched__event-body { -webkit-flex: 1; flex: 1; min-width: 0; }
.so-sched__event-title { font-weight: 800; }
.so-sched__event-title.so-t1 { font-size: 46px; }
.so-sched__event-title.so-t2 { font-size: 40px; }
.so-sched__event-title.so-t3 { font-size: 34px; }
.so-sched__event-title.so-t4 { font-size: 30px; }
.so-sched__event-place { margin-top: 4px; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; font-size: 30px; font-weight: 500; color: rgba(235,235,245,0.6); }
.so-sched__event-place .so-i { margin-right: 10px; }
.so-sched__event-place span { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 일정이 셋 이하면 한 줄이 높다 — 글자도 그만큼 키운다. */
.so-sched--few .so-sched__event-time { width: 270px; font-size: 84px; }
.so-sched--few .so-sched__event-title.so-t1 { font-size: 60px; }
.so-sched--few .so-sched__event-title.so-t2 { font-size: 52px; }
.so-sched--few .so-sched__event-title.so-t3 { font-size: 44px; }
.so-sched--few .so-sched__event-title.so-t4 { font-size: 38px; }
.so-sched--few .so-sched__event-place { margin-top: 8px; font-size: 34px; }

.so-sched__right { -webkit-flex: 0 0 440px; flex: 0 0 440px; width: 440px; margin-left: 32px; }

.so-sched__upcoming {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  padding: 32px 36px 12px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-sched__upcoming-head { -webkit-flex: none; flex: none; padding-bottom: 20px; }
.so-sched__upcoming-label { font-size: 28px; font-weight: 700; color: #bf5af2; }
.so-sched__upcoming-title { margin-top: 6px; font-size: 44px; font-weight: 800; line-height: 1.15; }

.so-sched__upcoming-body {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

/* 가운데 일정과 같은 줄 — 위에 선을 긋고 높이를 나눠 갖되, 한두 줄이 휑하게 늘지 않게 상한을 둔다. */
.so-sched__upcoming-item {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  max-height: 160px;
  min-height: 0;
  border-top: 2px solid #38383a;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  overflow: hidden;
}

.so-sched__upcoming-item .so-i { margin-right: 16px; font-size: 36px; color: #bf5af2; }
.so-sched__upcoming-item span { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.so-sched__stats { -webkit-flex: none; flex: none; margin-top: 32px; padding: 28px 32px 20px; }
.so-sched__stats h4 { margin-bottom: 8px; font-size: 28px; font-weight: 700; color: rgba(235,235,245,0.6); }
.so-sched__stats-grid { display: -webkit-flex; display: flex; -webkit-flex-wrap: wrap; flex-wrap: wrap; }
.so-sched__stat { width: 50%; padding: 8px 0; text-align: center; }
.so-sched__stat-value { font-weight: 800; line-height: 1.1; color: #64d2ff; -webkit-font-feature-settings: 'tnum'; font-feature-settings: 'tnum'; }
.so-sched__stat-value.so-t1 { font-size: 60px; }
.so-sched__stat-value.so-t2 { font-size: 48px; }
.so-sched__stat-value.so-t3 { font-size: 40px; }
.so-sched__stat-value.so-t4 { font-size: 34px; }
.so-sched__stat-label { font-size: 26px; font-weight: 600; color: rgba(235,235,245,0.6); }

/* 행사 카드가 없으면 통계가 오른쪽 단을 채우고 숫자를 가운데에 둔다. */
.so-sched__stats--fill {
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  margin-top: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
}

.so-sched__stats--fill .so-sched__stats-grid {
  -webkit-flex: 1;
  flex: 1;
  -webkit-align-content: center;
  align-content: center;
}

/* ── 대시보드 ─────────────────────────────────────────────────────── */
.so-dash { -webkit-flex-direction: column; flex-direction: column; }
.so-dash__kpis { -webkit-flex: none; flex: none; display: -webkit-flex; display: flex; }

.so-dash__kpi { -webkit-flex: 1 1 0; flex: 1 1 0; min-width: 0; padding: 28px 32px 24px; overflow: hidden; }
.so-dash__kpi + .so-dash__kpi { margin-left: 24px; }
/* 아이콘은 이름 앞에 — 오른쪽 끝에 홀로 떨어져 있으면 넓은 칸에서 값과 멀어진다. */
.so-dash__kpi-head { display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; }
.so-dash__kpi-label { -webkit-flex: 1; flex: 1; min-width: 0; font-size: 30px; font-weight: 600; color: rgba(235,235,245,0.6); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.so-dash__kpi-icon { width: 56px; height: 56px; margin-right: 18px; border-radius: 16px; font-size: 32px; }
.so-dash__kpi-value { margin-top: 12px; font-weight: 800; line-height: 1.05; letter-spacing: -0.02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; -webkit-font-feature-settings: 'tnum'; font-feature-settings: 'tnum'; }
.so-dash__kpi-value.so-t1 { font-size: 84px; }
.so-dash__kpi-value.so-t2 { font-size: 68px; }
.so-dash__kpi-value.so-t3 { font-size: 56px; }
.so-dash__kpi-value.so-t4 { font-size: 46px; }
.so-dash__kpi-change { margin-top: 8px; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; font-size: 28px; font-weight: 700; white-space: nowrap; overflow: hidden; }
.so-dash__kpi-change .so-i { margin-right: 8px; }
.so-dash__kpi-change .so-one { min-width: 0; }
.so-dash__kpi-change--up { color: #30d158; }
.so-dash__kpi-change--down { color: #ff453a; }

/* KPI 가 둘 이하 — 가로판. 아이콘 · 이름과 증감 · 값(오른쪽). */
.so-dash__kpi--wide { display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; padding: 32px 44px; }
.so-dash__kpi--wide .so-dash__kpi-icon { width: 96px; height: 96px; margin-right: 28px; border-radius: 26px; font-size: 52px; }
.so-dash__kpi-text { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-width: 0; }
.so-dash__kpi-text .so-dash__kpi-label { display: block; font-size: 34px; }
.so-dash__kpi--wide .so-dash__kpi-value { -webkit-flex: 0 1 auto; flex: 0 1 auto; max-width: 60%; min-width: 0; margin: 0 0 0 28px; }
.so-dash__kpi--wide .so-dash__kpi-value.so-t1 { font-size: 112px; }
.so-dash__kpi--wide .so-dash__kpi-value.so-t2 { font-size: 92px; }
.so-dash__kpi--wide .so-dash__kpi-value.so-t3 { font-size: 72px; }
.so-dash__kpi--wide .so-dash__kpi-value.so-t4 { font-size: 56px; }

.so-tone-blue { background: #0a84ff; }
.so-tone-green { background: #30d158; }
.so-tone-purple { background: #bf5af2; }
.so-tone-orange { background: #ff9f0a; }

.so-dash__charts { -webkit-flex: 1; flex: 1; min-height: 0; margin-top: 24px; display: -webkit-flex; display: flex; }

.so-dash__panel {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  padding: 28px 32px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-dash__panel + .so-dash__panel { margin-left: 24px; }
/*
 * 옆 패널이 있으면 차트는 KPI 두 칸 몫(사이 여백 포함)이다 — KPI 한 칸이 (100% - 여백 셋) / 4 라
 * 두 칸은 50% - 12px. 나머지를 순위·진행률이 똑같이 나눠 KPI 넷(또는 둘)과 세로 경계가 한 줄에
 * 선다. 2:1:1 비율로 나누던 때는 카드 안쪽 여백이 몫에 섞여 24px 어긋났다.
 */
.so-dash__panel--wide { -webkit-flex: 1 1 0; flex: 1 1 0; }
.so-dash__panel--half { -webkit-flex: none; flex: none; width: 50%; width: calc(50% - 12px); }
.so-dash__panel-head { -webkit-flex: none; flex: none; margin-bottom: 16px; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; -webkit-justify-content: space-between; justify-content: space-between; }
/* 제목이 범례·실시간 표시에 밀려 사라지지 않게 자리를 먼저 잡는다(범례가 줄어든다). */
.so-dash__panel-title { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-width: 0; font-size: 36px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.so-dash__legend { -webkit-flex: 0 1 auto; flex: 0 1 auto; min-width: 0; max-width: 55%; display: -webkit-flex; display: flex; margin-left: 20px; overflow: hidden; }
.so-dash__legend-item { -webkit-flex: 0 1 auto; flex: 0 1 auto; min-width: 0; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; font-size: 26px; font-weight: 600; color: rgba(235,235,245,0.7); }
.so-dash__legend-item .so-one { min-width: 0; }
.so-dash__legend-item + .so-dash__legend-item { margin-left: 24px; }
.so-dash__legend-dot { -webkit-flex: none; flex: none; width: 16px; height: 16px; margin-right: 10px; border-radius: 50%; }
.so-dot-blue { background: #0a84ff; }
.so-dot-green { background: #30d158; }
.so-dot-purple { background: #bf5af2; }
.so-dot-orange { background: #ff9f0a; }

.so-dash__bars { -webkit-flex: 1; flex: 1; min-height: 0; padding-top: 12px; display: -webkit-flex; display: flex; }

.so-dash__bar-group {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  min-width: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
}

/*
 * 막대 칸 — 막대는 절대 위치로 바닥에 세운다. flex 칸 안의 퍼센트 높이를 Chromium 39 는
 * 풀지 못해(막대가 0 이 되어 사라졌다) 칸의 실제 높이를 기준으로 하는 절대 위치로 잰다.
 *
 * 칸마다 25% 눈금선과 바닥선을 긋는다 — 칸이 붙어 있어 차트 폭 전체에 이어진 선이 된다.
 * 막대가 허공에 떠 보이지 않고, 높낮이를 눈금으로 비교할 수 있다.
 */
.so-dash__bar-slot {
  position: relative;
  -webkit-flex: 1;
  flex: 1;
  min-height: 0;
  border-bottom: 2px solid #48484a;
  background-image: linear-gradient(to bottom, rgba(255,255,255,0.08) 2px, transparent 2px);
  background-size: 100% 25%;
}

/* 막대 상자 — 칸 가운데, 넓은 칸에서도 막대가 뚱뚱해지지 않게 폭에 상한을 둔다. */
.so-dash__bar-box { position: absolute; top: 0; bottom: 0; left: 0; right: 0; width: 72%; max-width: 168px; margin: 0 auto; }
.so-dash__bar-box--solo { width: 48%; max-width: 112px; }
.so-dash__bar { position: absolute; bottom: 0; border-radius: 12px 12px 0 0; }
.so-dash__bar--solo { left: 0; width: 100%; }
.so-dash__bar--first { left: 0; width: 46%; }
.so-dash__bar--second { right: 0; width: 46%; }
.so-bar-blue { background: #0a84ff; }
.so-bar-green { background: #30d158; }
.so-bar-purple { background: #bf5af2; }
.so-bar-orange { background: #ff9f0a; }
.so-dash__bar-label { -webkit-flex: none; flex: none; margin-top: 12px; font-size: 26px; font-weight: 600; color: rgba(235,235,245,0.7); text-align: center; white-space: nowrap; overflow: hidden; }

.so-dash__live { display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; -webkit-flex: none; flex: none; margin-left: 16px; font-size: 26px; font-weight: 800; color: #30d158; }
.so-dash__live-dot { width: 14px; height: 14px; margin-right: 10px; border-radius: 50%; background: #30d158; -webkit-animation: so-blink 1.2s ease-in-out infinite; animation: so-blink 1.2s ease-in-out infinite; }

/*
 * 순위와 진행률은 같은 줄이다 — 위에 선을 긋고, 높이를 나눠 갖되 상한을 둔다. 줄 수가 같으면
 * 두 패널의 줄이 나란히 선다(예전에는 순위는 위로 붙고 진행률은 위아래로 흩어졌다).
 */
.so-dash__rows { -webkit-flex: 1; flex: 1; min-height: 0; display: -webkit-flex; display: flex; -webkit-flex-direction: column; flex-direction: column; overflow: hidden; }

.so-dash__row {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  max-height: 160px;
  min-height: 0;
  border-top: 2px solid #38383a;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  overflow: hidden;
}

.so-dash__rank {
  -webkit-flex: none;
  flex: none;
  width: 60px;
  height: 60px;
  margin-right: 20px;
  border-radius: 18px;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 800;
}

.so-rank-1 { background: #ffd60a; color: #000; }
.so-rank-2 { background: #c7c7cc; color: #000; }
.so-rank-3 { background: #ff9f0a; color: #000; }
.so-rank-n { background: #3a3a3c; color: #fff; }

.so-dash__row-body { -webkit-flex: 1; flex: 1; min-width: 0; }
.so-dash__row-name { font-size: 34px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.so-dash__row-dept { font-size: 26px; font-weight: 500; color: rgba(235,235,245,0.6); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* 점수가 길어도 이름을 밀어내지 않는다 — 점수는 칸의 절반까지만. */
.so-dash__row-score { -webkit-flex: 0 1 auto; flex: 0 1 auto; max-width: 45%; min-width: 0; margin-left: 16px; font-size: 36px; font-weight: 800; color: #30d158; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 진행률 한 줄 — 이름·퍼센트 아래에 막대. 순위 줄과 같은 칸에 세로로 쌓는다. */
.so-dash__progress-item { -webkit-flex-direction: column; flex-direction: column; -webkit-align-items: stretch; align-items: stretch; -webkit-justify-content: center; justify-content: center; }
.so-dash__progress-head { display: -webkit-flex; display: flex; -webkit-align-items: baseline; align-items: baseline; }
.so-dash__progress-name { -webkit-flex: 1; flex: 1; min-width: 0; margin-right: 12px; font-size: 30px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.so-dash__progress-pct { -webkit-flex: none; flex: none; font-size: 34px; font-weight: 800; -webkit-font-feature-settings: 'tnum'; font-feature-settings: 'tnum'; }
.so-dash__progress-track { margin-top: 12px; height: 18px; border-radius: 999px; background: #3a3a3c; overflow: hidden; }
.so-dash__progress-fill { height: 100%; border-radius: 999px; }
.so-fill-blue { background: #0a84ff; }
.so-fill-green { background: #30d158; }
.so-fill-purple { background: #bf5af2; }
.so-fill-orange { background: #ff9f0a; }

/* ── 반반 화면 ───────────────────────────────────────────────────────
 * 그림 칸과 글 칸을 반씩. 칸 크기는 px 로 — 옛 웹뷰(Chromium 39)는 세로 flex 의 % 기준 크기를 0 으로 읽는다.
 * 본문은 가로 캔버스 1840×852, 세로 캔버스 1016×1692 다. 방향은 \`so-split--auto\`(화면에 맞춰 — 가로 화면은 좌우,
 * 세로 화면은 위아래) · \`--columns\`(좌우) · \`--rows\`(위아래). 가로 화면의 위아래 판은 글 칸이 낮고 넓어 글과 항목을
 * 옆으로 놓는다(아래 \`:not(--portrait)\`).
 */
.so-split { -webkit-flex-direction: row; flex-direction: row; }
.so-split__pane { -webkit-flex: 0 0 904px; flex: 0 0 904px; width: 904px; min-width: 0; min-height: 0; }
.so-split__pane + .so-split__pane { margin-left: 32px; }

.so-split--rows { -webkit-flex-direction: column; flex-direction: column; }
.so-split--rows .so-split__pane { -webkit-flex: 0 0 410px; flex: 0 0 410px; width: auto; height: 410px; }
.so-split--rows .so-split__pane + .so-split__pane { margin: 32px 0 0; }

.so-split__media { position: relative; overflow: hidden; border-radius: 36px; background: #1c1c1e; }
.so-split__media--contain { background: #000; }

.so-split__image,
.so-split__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.so-split__media .so-fit-contain { object-fit: contain; }

/*
 * 올린 HTML 칸(2026-11-03) — 받는 쪽이 이 자리에 샌드박스 iframe 을 넣는다(\`data-so-html\`). 늘고 주는 HTML 은 칸을 꽉 채우고,
 * 고정 크기로 만든 HTML 은 그 크기로 그려 칸에 맞춘다(크기·자리·배율은 iframe 의 style 에 — 남는 곳은 검게). 바탕은 흰색 —
 * 바탕색을 정하지 않은 문서가 브라우저에서처럼 보이게. 장식 무늬 위에 덮여, 못 읽으면 받는 쪽이 이 칸을 걷는다.
 */
.so-split__html { position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden; background: #000; }
.so-split__html > iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
  -webkit-transform-origin: 0 0;
  transform-origin: 0 0;
}

.so-split__placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: center;
  justify-content: center;
  background: #5e5ce6;
  background: linear-gradient(135deg, #0a84ff, #5e5ce6 45%, #ff375f);
  color: rgba(255,255,255,0.5);
  font-size: 200px;
}

.so-split__text {
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  padding: 56px 64px 48px;
  overflow: hidden;
}

.so-split__copy {
  -webkit-flex: 1 1 auto;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

/* 가운데에 두되 넘치면 위부터 — justify-content: center 는 넘칠 때 위아래가 함께 잘린다(제목 첫 줄이 사라진다). */
.so-split__copy > :first-child { margin-top: auto; }
.so-split__copy > :last-child { margin-bottom: auto; }

.so-split__badge { -webkit-flex: none; flex: none; margin-bottom: 28px; }

.so-split__title { font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; }
.so-split__title.so-t1 { font-size: 96px; }
.so-split__title.so-t2 { font-size: 80px; }
.so-split__title.so-t3 { font-size: 66px; }
.so-split__title.so-t4 { font-size: 54px; }

/* \`.so-tpl-canvas p { margin: 0 }\` 보다 앞서게 칸 이름을 붙인다. */
.so-split__copy .so-split__lead { margin-top: 24px; }
.so-split__lead { color: rgba(235,235,245,0.7); line-height: 1.45; font-weight: 500; }
.so-split__lead.so-t1 { font-size: 40px; }
.so-split__lead.so-t2 { font-size: 34px; }
.so-split__lead.so-t3 { font-size: 30px; }
.so-split__lead.so-t4 { font-size: 28px; }

.so-split__aside {
  -webkit-flex: 0 1 auto;
  flex: 0 1 auto;
  min-width: 0;
  min-height: 0;
  margin-top: 32px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}

.so-split__points { -webkit-flex: 0 1 auto; flex: 0 1 auto; min-height: 0; overflow: hidden; }

.so-split__point { display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; min-width: 0; }
.so-split__point + .so-split__point { margin-top: 18px; }

.so-split__point-icon {
  width: 56px;
  height: 56px;
  margin-right: 24px;
  border-radius: 16px;
  background: #0a84ff;
  background: linear-gradient(135deg, #0a84ff, #5e5ce6);
  font-size: 32px;
}

.so-split__point-text { -webkit-flex: 1; flex: 1; min-width: 0; font-weight: 700; line-height: 1.25; }
.so-split__point-text.so-t1 { font-size: 40px; }
.so-split__point-text.so-t2 { font-size: 36px; }
.so-split__point-text.so-t3 { font-size: 32px; }
.so-split__point-text.so-t4 { font-size: 30px; }

.so-split__note {
  -webkit-flex: none;
  flex: none;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  min-width: 0;
  font-size: 32px;
  font-weight: 600;
  color: rgba(235,235,245,0.8);
}

.so-split__points + .so-split__note { margin-top: 28px; padding-top: 28px; border-top: 2px solid #38383a; }
.so-split__note .so-i { margin-right: 14px; color: #0a84ff; }

/* 붐비는 글 칸 — 한 단계 작게(렌더러가 항목 수·글 길이로 붙인다). */
.so-split--dense .so-split__badge { margin-bottom: 20px; }
.so-split--dense .so-split__title.so-t1 { font-size: 84px; }
.so-split--dense .so-split__title.so-t2 { font-size: 68px; }
.so-split--dense .so-split__title.so-t3 { font-size: 56px; }
.so-split--dense .so-split__title.so-t4 { font-size: 46px; }
.so-split--dense .so-split__copy .so-split__lead { margin-top: 18px; -webkit-line-clamp: 2; }
.so-split--dense .so-split__lead.so-t1 { font-size: 34px; }
.so-split--dense .so-split__lead.so-t2,
.so-split--dense .so-split__lead.so-t3,
.so-split--dense .so-split__lead.so-t4 { font-size: 30px; }
.so-split--dense .so-split__aside { margin-top: 24px; }
.so-split--dense .so-split__point + .so-split__point { margin-top: 12px; }
.so-split--dense .so-split__point-icon { width: 48px; height: 48px; margin-right: 20px; font-size: 28px; }
.so-split--dense .so-split__point-text { -webkit-line-clamp: 1; }
.so-split--dense .so-split__point-text.so-t1,
.so-split--dense .so-split__point-text.so-t2 { font-size: 32px; }
.so-split--dense .so-split__point-text.so-t3,
.so-split--dense .so-split__point-text.so-t4 { font-size: 30px; }
.so-split--dense .so-split__points + .so-split__note { margin-top: 20px; padding-top: 20px; }

/* 가로 화면의 위아래 판 — 글 칸이 1840×410 이라 글(왼쪽)과 항목·한 줄(오른쪽)을 옆으로 놓고 한 단계 작게. */
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__text {
  -webkit-flex-direction: row;
  flex-direction: row;
  -webkit-align-items: center;
  align-items: center;
  padding: 40px 56px;
}
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__copy { -webkit-flex: 1 1 0; flex: 1 1 0; max-height: 100%; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__badge { margin-bottom: 18px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__title { -webkit-line-clamp: 2; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__title.so-t1 { font-size: 72px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__title.so-t2 { font-size: 64px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__title.so-t3 { font-size: 56px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__title.so-t4 { font-size: 48px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__copy .so-split__lead { margin-top: 16px; -webkit-line-clamp: 2; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__lead.so-t1 { font-size: 30px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__lead.so-t2,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__lead.so-t3,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__lead.so-t4 { font-size: 28px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__aside {
  -webkit-flex: 0 0 720px;
  flex: 0 0 720px;
  width: 720px;
  max-height: 100%;
  margin: 0 0 0 48px;
  -webkit-justify-content: center;
  justify-content: center;
}
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point + .so-split__point { margin-top: 12px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point-icon { width: 48px; height: 48px; margin-right: 20px; font-size: 28px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point-text { -webkit-line-clamp: 1; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point-text.so-t1,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point-text.so-t2 { font-size: 32px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point-text.so-t3,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__point-text.so-t4 { font-size: 30px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__points + .so-split__note { margin-top: 20px; padding-top: 20px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows .so-split__note { font-size: 30px; }

/* ── 무재해 현황 ─────────────────────────────────────────────────────
 * 위 큰 카드(배지·제목·부서·시작일 | D-day)가 남는 높이를 갖고, 숫자 다섯 칸과 진행률 카드는 제 높이다. 숫자 칸은 1840 폭을
 * 다섯으로 나눈 290px 안쪽에 들어가야 해서 달성 예정일(2027.02.28)만 한 단계 작게 쓴다.
 */
.so-zero { -webkit-flex-direction: column; flex-direction: column; }

.so-zero__hero {
  -webkit-flex: 1 1 auto;
  flex: 1 1 auto;
  min-height: 0;
  padding: 36px 56px;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  overflow: hidden;
}

.so-zero__intro { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-width: 0; }
.so-zero__badge { margin-bottom: 16px; }
.so-zero__title { font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; }
.so-zero__title.so-t1 { font-size: 96px; }
.so-zero__title.so-t2 { font-size: 84px; }
.so-zero__title.so-t3 { font-size: 72px; }
.so-zero__title.so-t4 { font-size: 60px; }

.so-zero__meta { margin-top: 20px; display: -webkit-flex; display: flex; -webkit-align-items: center; align-items: center; min-width: 0; }
.so-zero__meta-item {
  -webkit-flex: none;
  flex: none;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: center;
  align-items: center;
  font-size: 34px;
  font-weight: 600;
  color: rgba(235,235,245,0.8);
  white-space: nowrap;
}
/* 부서 이름이 길면 그것만 줄인다 — 시작일·누적 일수가 밀려 사라지지 않게. */
.so-zero__meta-item:first-child { -webkit-flex: 0 1 auto; flex: 0 1 auto; min-width: 0; }
.so-zero__meta-item .so-one { min-width: 0; }
.so-zero__meta-item + .so-zero__meta-item { margin-left: 48px; }
.so-zero__meta-item .so-i { margin-right: 14px; color: #0a84ff; }
.so-zero__meta-item b { color: #fff; font-weight: 800; }

.so-zero__dday { -webkit-flex: none; flex: none; margin-left: 48px; text-align: right; }
.so-zero__dday-label { font-size: 34px; font-weight: 700; color: rgba(235,235,245,0.7); }
.so-zero__dday-value {
  margin-top: 4px;
  font-size: 150px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
  white-space: nowrap;
  -webkit-font-feature-settings: 'tnum';
  font-feature-settings: 'tnum';
  color: #30d158;
}
.so-zero__dday--before .so-zero__dday-value { color: #0a84ff; }
.so-zero__dday--today .so-zero__dday-value,
.so-zero__dday--done .so-zero__dday-value { color: #ffd60a; }

.so-zero__kpis { -webkit-flex: none; flex: none; height: 200px; margin-top: 24px; display: -webkit-flex; display: flex; }
.so-zero__kpi {
  -webkit-flex: 1 1 0;
  flex: 1 1 0;
  min-width: 0;
  padding: 24px 28px;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-justify-content: center;
  justify-content: center;
  overflow: hidden;
}
.so-zero__kpi + .so-zero__kpi { margin-left: 24px; }
.so-zero__kpi-label { font-size: 30px; font-weight: 600; color: rgba(235,235,245,0.6); }
.so-zero__kpi-value {
  margin-top: 12px;
  font-size: 76px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  -webkit-font-feature-settings: 'tnum';
  font-feature-settings: 'tnum';
}
.so-zero__kpi-value--date { font-size: 50px; line-height: 1.6; }
.so-zero__unit { margin-left: 6px; font-size: 40px; font-weight: 700; color: rgba(235,235,245,0.6); }
.so-zero__kpi--accent { background: #0a84ff; }
.so-zero__kpi--accent .so-zero__kpi-label,
.so-zero__kpi--accent .so-zero__unit { color: rgba(255,255,255,0.8); }

.so-zero__progress { -webkit-flex: none; flex: none; margin-top: 24px; padding: 28px 44px; }
.so-zero__progress-head {
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: baseline;
  align-items: baseline;
  -webkit-justify-content: space-between;
  justify-content: space-between;
}
.so-zero__progress-title { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-width: 0; font-size: 40px; font-weight: 800; }
.so-zero__progress-pct {
  -webkit-flex: none;
  flex: none;
  margin-left: 24px;
  font-size: 56px;
  font-weight: 800;
  -webkit-font-feature-settings: 'tnum';
  font-feature-settings: 'tnum';
}
.so-zero__track { margin-top: 20px; height: 48px; border-radius: 999px; background: #3a3a3c; overflow: hidden; }
.so-zero__fill { height: 100%; border-radius: 999px; background: #30d158; background: linear-gradient(90deg, #0a84ff, #30d158); }
.so-zero__progress-foot {
  margin-top: 18px;
  display: -webkit-flex;
  display: flex;
  -webkit-align-items: baseline;
  align-items: baseline;
  -webkit-justify-content: space-between;
  justify-content: space-between;
}
.so-zero__status { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-width: 0; font-size: 34px; font-weight: 700; }
.so-zero__period { -webkit-flex: none; flex: none; margin-left: 32px; font-size: 30px; font-weight: 600; color: rgba(235,235,245,0.6); white-space: nowrap; }

/* ── 세로 화면 ───────────────────────────────────────────────────────
 * 가로판의 옆으로 놓인 단을 위아래로 쌓는다. 글자 크기·카드 모양은 가로판 그대로 —
 * 1080 폭에서도 가장 긴 줄이 들어가게 단마다 높이만 다시 나눈다. 가로판 규칙을 덮어쓰기만
 * 하므로, 세로판이 없는 조각은 가로판 모양으로 남는다(깨지지 않는다).
 */
.so-tpl-canvas--portrait .so-tpl__main { padding: 4px 32px 28px; }
.so-tpl-canvas--portrait .so-tpl__header { padding: 0 40px; }

/* 공지 — 본문 위, 공지 목록·날씨 아래 */
.so-tpl-canvas--portrait .so-ann { -webkit-flex-direction: column; flex-direction: column; }
.so-tpl-canvas--portrait .so-ann__main { -webkit-flex: 1 1 auto; flex: 1 1 auto; min-height: 0; padding: 48px 52px 40px; }
.so-tpl-canvas--portrait .so-ann__side { -webkit-flex: 0 0 760px; flex: 0 0 760px; width: auto; height: 760px; margin: 32px 0 0; }

/* 프로모션 — 그림 위, 혜택·마감 아래 */
.so-tpl-canvas--portrait .so-promo { -webkit-flex-direction: column; flex-direction: column; }
/* 높이는 px 로 — 옛 웹뷰(Chromium 39)는 세로 flex 의 % 기준 크기를 0 으로 읽어 그림 카드가 한 줄로 접혔다. */
.so-tpl-canvas--portrait .so-promo__visual { -webkit-flex: 0 0 820px; flex: 0 0 820px; width: auto; height: 820px; }
.so-tpl-canvas--portrait .so-promo__side { -webkit-flex: 1; flex: 1; min-height: 0; margin: 32px 0 0; }

/* 긴급 — 이미 위아래로 쌓인다. 수치는 세 칸이 1080 폭을 나눠 갖도록 한 단계 작게,
   남는 높이는 할 일 줄이 나눠 가진다(가로판의 150px 상한이면 아래 절반이 빈다). */
.so-tpl-canvas--portrait .so-alert__stat-value.so-t1 { font-size: 72px; }
.so-tpl-canvas--portrait .so-alert__stat-value.so-t2 { font-size: 60px; }
.so-tpl-canvas--portrait .so-alert__stat-value.so-t3 { font-size: 50px; }
.so-tpl-canvas--portrait .so-alert__stat-value.so-t4 { font-size: 42px; }
.so-tpl-canvas--portrait .so-alert__action { max-height: 300px; }
.so-tpl-canvas--portrait .so-alert__action-text.so-t1 { font-size: 54px; }
.so-tpl-canvas--portrait .so-alert__action-text.so-t2 { font-size: 46px; }
.so-tpl-canvas--portrait .so-alert__action-text.so-t3 { font-size: 40px; }

/* 일정 — 오늘 띠(날짜 · 남은 시간)가 맨 위, 일정 목록, 다가오는 일정·통계가 아래 한 줄 */
.so-tpl-canvas--portrait .so-sched { -webkit-flex-direction: column; flex-direction: column; }
.so-tpl-canvas--portrait .so-sched__today { -webkit-flex: none; flex: none; width: auto; -webkit-flex-direction: row; flex-direction: row; }
.so-tpl-canvas--portrait .so-sched__date { padding: 24px 0; }
.so-tpl-canvas--portrait .so-sched__date-day { font-size: 180px; }
.so-tpl-canvas--portrait .so-sched__countdown { -webkit-flex: 0 0 50%; flex: 0 0 50%; display: -webkit-flex; display: flex; -webkit-flex-direction: column; flex-direction: column; -webkit-justify-content: center; justify-content: center; }
.so-tpl-canvas--portrait .so-sched__list { -webkit-flex: 1; flex: 1; min-height: 0; margin: 32px 0 0; }
.so-tpl-canvas--portrait .so-sched__right { -webkit-flex: none; flex: none; width: auto; margin: 32px 0 0; -webkit-flex-direction: row; flex-direction: row; }
.so-tpl-canvas--portrait .so-sched__right > * { -webkit-flex: 1 1 0; flex: 1 1 0; min-width: 0; }
.so-tpl-canvas--portrait .so-sched__right > * + * { margin: 0 0 0 32px; }

/* 반반 — 화면에 맞추면 위아래(그림 위·글 아래, 830px 씩). 좌우로 고르면 492px 폭의 좁고 긴 칸이라 글을 한 단계 작게, 줄은 더 많이. */
.so-tpl-canvas--portrait .so-split--auto { -webkit-flex-direction: column; flex-direction: column; }
.so-tpl-canvas--portrait .so-split--auto .so-split__pane,
.so-tpl-canvas--portrait .so-split--rows .so-split__pane { -webkit-flex: 0 0 830px; flex: 0 0 830px; width: auto; height: 830px; }
.so-tpl-canvas--portrait .so-split--auto .so-split__pane + .so-split__pane { margin: 32px 0 0; }
.so-tpl-canvas--portrait .so-split--columns .so-split__pane { -webkit-flex: 0 0 492px; flex: 0 0 492px; width: 492px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__text { padding: 48px 40px 40px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__title { -webkit-line-clamp: 6; }
.so-tpl-canvas--portrait .so-split--columns .so-split__title.so-t1 { font-size: 72px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__title.so-t2 { font-size: 60px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__title.so-t3 { font-size: 50px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__title.so-t4 { font-size: 42px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__lead { -webkit-line-clamp: 8; }
.so-tpl-canvas--portrait .so-split--columns .so-split__lead.so-t1 { font-size: 32px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__lead.so-t2 { font-size: 30px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__lead.so-t3 { font-size: 28px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__lead.so-t4 { font-size: 26px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__point-icon { width: 48px; height: 48px; margin-right: 16px; font-size: 28px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__point-text { -webkit-line-clamp: 3; }
.so-tpl-canvas--portrait .so-split--columns .so-split__point-text.so-t1 { font-size: 32px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__point-text.so-t2 { font-size: 30px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__point-text.so-t3,
.so-tpl-canvas--portrait .so-split--columns .so-split__point-text.so-t4 { font-size: 28px; }
.so-tpl-canvas--portrait .so-split--columns .so-split__note { font-size: 28px; }

/* 대시보드 — 지표 넷을 두 줄로, 그래프 판은 위아래로 */
.so-tpl-canvas--portrait .so-dash__kpis { -webkit-flex-wrap: wrap; flex-wrap: wrap; }
.so-tpl-canvas--portrait .so-dash__kpi { -webkit-flex: none; flex: none; width: 48%; width: calc(50% - 12px); }
.so-tpl-canvas--portrait .so-dash__kpi + .so-dash__kpi { margin-left: 0; }
.so-tpl-canvas--portrait .so-dash__kpi:nth-child(2n) { margin-left: 24px; }
.so-tpl-canvas--portrait .so-dash__kpi:nth-child(n+3) { margin-top: 24px; }
.so-tpl-canvas--portrait .so-dash__kpi--wide { width: 100%; }
.so-tpl-canvas--portrait .so-dash__charts { -webkit-flex-direction: column; flex-direction: column; }
.so-tpl-canvas--portrait .so-dash__panel + .so-dash__panel { margin: 24px 0 0; }
.so-tpl-canvas--portrait .so-dash__panel--half { -webkit-flex: 1 1 0; flex: 1 1 0; width: auto; }

/* 무재해 — 위 카드는 글 아래 D-day, 부서·시작일·누적은 한 줄씩. 숫자 칸은 둘씩 두 줄, 달성률은 한 줄을 다 쓴다. */
.so-tpl-canvas--portrait .so-zero__hero { -webkit-flex-direction: column; flex-direction: column; -webkit-align-items: stretch; align-items: stretch; -webkit-justify-content: center; justify-content: center; padding: 48px; }
.so-tpl-canvas--portrait .so-zero__meta { display: block; }
.so-tpl-canvas--portrait .so-zero__meta-item + .so-zero__meta-item { margin: 12px 0 0; }
.so-tpl-canvas--portrait .so-zero__dday { margin: 40px 0 0; text-align: left; }
.so-tpl-canvas--portrait .so-zero__kpis { height: auto; -webkit-flex-wrap: wrap; flex-wrap: wrap; }
.so-tpl-canvas--portrait .so-zero__kpi { -webkit-flex: none; flex: none; width: 48%; width: calc(50% - 12px); height: 200px; }
.so-tpl-canvas--portrait .so-zero__kpi + .so-zero__kpi { margin-left: 0; }
.so-tpl-canvas--portrait .so-zero__kpi:nth-child(2n) { margin-left: 24px; }
.so-tpl-canvas--portrait .so-zero__kpi:nth-child(n+3) { margin-top: 24px; }
.so-tpl-canvas--portrait .so-zero__kpi:nth-child(5) { width: 100%; }
.so-tpl-canvas--portrait .so-zero__progress-foot { display: block; }
.so-tpl-canvas--portrait .so-zero__period { display: block; margin: 10px 0 0; }

/* ── 반반 화면의 칸 몫 ────────────────────────────────────────────────
 * 그림 칸을 키우면(60·70) 글 칸이 좁아지거나(좌우) 낮아진다(위아래) — 글을 한 단계씩 작게, 줄은 덜. 칸 크기는 \`splitShareCss\`.
 */
${splitShareCss()}

${enterCss()}

/* ── 구역 나누기 ─────────────────────────────────────────────────────
 * 본문을 2~4 구역으로(2026-11-03). 구역의 자리·크기는 \`ZONE_BOXES\` 가 px 로 적는다(아래 생성 규칙). 그림 구역에 여러 장이 있으면
 * 장(\`so-zone__slide\`)을 겹쳐 두고 지금 장만 보인다(\`--on\`) — 감춘 장도 자리를 지켜(visibility) HTML 칸이 제 크기로 뜬다.
 * 넘기기는 한 번 도는 페이드다(끝없는 애니메이션은 옛 웹뷰의 UI 스레드를 잡는다).
 */
.so-zones { position: relative; }
.so-zone { position: absolute; overflow: hidden; border-radius: 36px; }
.so-zone--media { background: #1c1c1e; }
.so-zone__slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;
  opacity: 0;
  -webkit-transition: opacity 0.6s ease, visibility 0s linear 0.6s;
  transition: opacity 0.6s ease, visibility 0s linear 0.6s;
}
.so-zone__slide--on {
  visibility: visible;
  opacity: 1;
  -webkit-transition: opacity 0.6s ease;
  transition: opacity 0.6s ease;
}
.so-zone__slide--contain { background: #000; }
.so-zone .so-fit-contain { object-fit: contain; }

.so-zone--text {
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  overflow: hidden;
}
/* 가운데에 두되 넘치면 위부터 — 반반 화면의 글 칸과 같은 까닭. */
.so-zone--text > :first-child { margin-top: auto; }
.so-zone--text > :last-child { margin-bottom: auto; }
.so-zone__badge { -webkit-flex: none; flex: none; margin-bottom: 24px; }
.so-zone__title { font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; }
.so-zone__lead { margin-top: 20px; color: rgba(235,235,245,0.7); line-height: 1.45; font-weight: 500; }

${zonesCss()}

/* 가로 화면 좌우 — 글 칸 723px(60) · 542px(70). 542 는 세로 화면 좌우 칸(492px)과 같은 크기의 글로. */
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s60:not(.so-split--rows) .so-split__title.so-t1 { font-size: 80px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s60:not(.so-split--rows) .so-split__title.so-t2 { font-size: 66px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s60:not(.so-split--rows) .so-split__title.so-t3 { font-size: 56px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s60:not(.so-split--rows) .so-split__title.so-t4 { font-size: 46px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__text { padding: 48px 40px 40px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__title { -webkit-line-clamp: 4; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__title.so-t1 { font-size: 72px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__title.so-t2 { font-size: 60px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__title.so-t3 { font-size: 50px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__title.so-t4 { font-size: 42px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__lead { -webkit-line-clamp: 3; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__lead.so-t1 { font-size: 32px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__lead.so-t2,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__lead.so-t3,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__lead.so-t4 { font-size: 28px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__point-icon { width: 48px; height: 48px; margin-right: 16px; font-size: 28px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__point-text { -webkit-line-clamp: 2; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__point-text.so-t1,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__point-text.so-t2 { font-size: 32px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__point-text.so-t3,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__point-text.so-t4 { font-size: 28px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--s70:not(.so-split--rows) .so-split__note { font-size: 28px; }

/* 가로 화면 위아래 — 글 칸 328px(60) · 246px(70). 제목·설명은 한 줄씩, 70 이면 핵심 항목은 둘까지. */
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s60 .so-split__text,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__text { padding-top: 28px; padding-bottom: 28px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s60 .so-split__lead,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__lead { -webkit-line-clamp: 1; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__badge { display: none; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__title { -webkit-line-clamp: 1; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__title.so-t1 { font-size: 60px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__title.so-t2 { font-size: 54px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__title.so-t3,
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__title.so-t4 { font-size: 46px; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__point:nth-child(n+3) { display: none; }
.so-tpl-canvas:not(.so-tpl-canvas--portrait) .so-split--rows.so-split--s70 .so-split__point + .so-split__point { margin-top: 8px; }

/* 세로 화면 좌우 — 글 칸 394px(60) · 295px(70). 글을 더 작게, 항목은 한 줄. */
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__text,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__text { padding: 40px 28px 32px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__title.so-t1,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t1 { font-size: 56px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__title.so-t2,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t2 { font-size: 48px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__title.so-t3,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t3 { font-size: 42px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__title.so-t4,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t4 { font-size: 36px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t1 { font-size: 44px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t2 { font-size: 40px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t3,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__title.so-t4 { font-size: 34px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__lead,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__lead { font-size: 26px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__point-icon,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__point-icon { display: none; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__point-text,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__point-text { font-size: 26px; }
.so-tpl-canvas--portrait .so-split--columns.so-split--s60 .so-split__note,
.so-tpl-canvas--portrait .so-split--columns.so-split--s70 .so-split__note { font-size: 26px; }

/* ── 떠 있는 동안의 움직임 ─────────────────────────────────────────────
 * \`so-live\` 아래에서만(\`liveCss\`). 맨 끝에 둔다 — 카드·줄의 자리·잘림을 덮어써야 빛이 상자 안에서 돈다.
 */
${liveCss()}
`;
//# sourceMappingURL=styles.js.map